import { type PixelCoordinates } from '~/_shared/types/coordinateSystem/coordinateSystem';
import { clamp } from '~/_shared/utils/number/number.helpers';

export const SNAP_RADIUS_PX = 12;

const pixelDistanceSquared = (a: PixelCoordinates, b: PixelCoordinates): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return (dx * dx) + (dy * dy);
};

const closestPointOnSegment = ({ point, start, end }: {
  point: PixelCoordinates;
  start: PixelCoordinates;
  end: PixelCoordinates;
}): PixelCoordinates => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = (dx * dx) + (dy * dy);

  if (lengthSquared === 0) {
    return start;
  }

  const dot = ((point.x - start.x) * dx) + ((point.y - start.y) * dy);
  const t = clamp(dot / lengthSquared, { min: 0, max: 1 });
  return { x: start.x + (t * dx), y: start.y + (t * dy) };
};

// Vertices take priority over edges. Each chain is expected to repeat its first
// point as its last when closed, so edges (including the closing one) are just
// consecutive pairs and no separate handling for closed vs open chains is needed.
//// Measured ~2ms for a full worst-case execution (no match found, so both loops run to
//// completion) over 98904 points across 652 chains (M4 Mac) — plenty of headroom before
//// this would need a spatial index instead of the current brute-force scan.
export const findSnapPixel = (
  candidate: PixelCoordinates,
  chains: ReadonlyArray<ReadonlyArray<PixelCoordinates>>,
  radiusPx: number = SNAP_RADIUS_PX,
): PixelCoordinates | null => {
  const radiusPxSquared = radiusPx * radiusPx;

  let nearestVertex: PixelCoordinates | null = null;
  let nearestVertexDistanceSquared = Infinity;

  for (const chain of chains) {
    for (const vertex of chain) {
      const distanceSquared = pixelDistanceSquared(candidate, vertex);
      if (distanceSquared < nearestVertexDistanceSquared) {
        nearestVertexDistanceSquared = distanceSquared;
        nearestVertex = vertex;
      }
    }
  }

  if (nearestVertex && nearestVertexDistanceSquared <= radiusPxSquared) {
    return nearestVertex;
  }

  let nearestOnEdge: PixelCoordinates | null = null;
  let nearestEdgeDistanceSquared = Infinity;

  for (const chain of chains) {
    for (let i = 0; i < chain.length - 1; i++) {
      const start = chain[i];
      const end = chain[i + 1];
      if (!start || !end) {
        continue;
      }

      const closest = closestPointOnSegment({ point: candidate, start, end });
      const distanceSquared = pixelDistanceSquared(candidate, closest);
      if (distanceSquared < nearestEdgeDistanceSquared) {
        nearestEdgeDistanceSquared = distanceSquared;
        nearestOnEdge = closest;
      }
    }
  }

  if (nearestOnEdge && nearestEdgeDistanceSquared <= radiusPxSquared) {
    return nearestOnEdge;
  }

  return null;
};
